// Your rooms (MAJLIS-126; D-55, UI-C35, P-35, P-36): the rooms THIS PHONE hosted or joined, kept in its own storage and
// never sent anywhere as a list. For each room it keeps the room id, the role, the names the server last gave (the
// room's and its host's), the last visit, the listing handle the room's own seats were told (ruling 11: so a room of
// yours that is listed shows once, here) and when the phone first learned the room ended. NEVER THE CODE: nothing here
// is given one, and yourRooms.test.ts searches the stored value for it. At most YOUR_ROOMS_MAX rooms, most recent first
// (ASSUMPTION); an ended room is kept ENDED_ROOM_KEEP_MS after the phone learned it ended (ASSUMPTION), then dropped;
// a room the server leaves out of POST /api/rooms/mine (not ours any more, deleted, unknown) is dropped at once.
import { ENDED_ROOM_KEEP_MS, YOUR_ROOMS_MAX } from "../constants";
import type { KeyValue } from "../protocol/identity";
import type { MineRoom } from "../protocol/rooms";

export const YOUR_ROOMS_KEY = "majlis.rooms";

export interface YourRoom {
  room_id: string;
  role: "host" | "joined";
  /** the room's name as the server last gave it; null: never listed (shown as "{host}'s majlis") */
  name: string | null;
  /** the host's name as the server last gave it (null: not known yet) */
  host: string | null;
  /** the last visit, wall ms */
  at: number;
  /** ruling 11: the room's listing handle while listed, as its own seats were told */
  handle: string | null;
  /** wall ms this phone first learned the room ended; null while active */
  ended: number | null;
  /** the seated count the server last gave (refreshed by every answer of POST /api/rooms/mine) */
  seated: number;
  /** MAJLIS-145 (UI-C35): wall ms of this phone's FIRST visit - the start time's FALLBACK while the server has not
   * given `created`. A row stored before MAJLIS-145 falls back to its last visit. */
  first: number;
  /** MAJLIS-145 (P-35): wall ms the ROOM was created, as POST /api/rooms/mine last gave it (null: not given yet) */
  created: number | null;
  /** MAJLIS-145 (UI-C35, P-38): room_state.passive as the room last said it (false until it does) */
  passive: boolean;
}

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const isoMs = (v: unknown): number | null => {
  const ms = typeof v === "string" ? Date.parse(v) : NaN;
  return Number.isFinite(ms) ? ms : null;
};
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

function parse(raw: string | null): YourRoom[] {
  if (!raw) return [];
  let list: unknown;
  try {
    list = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(list)) return [];
  const out: YourRoom[] = [];
  for (const v of list) {
    const o = (v ?? {}) as Record<string, unknown>;
    const id = str(o.room_id);
    const at = num(o.at);
    if (!id || at === null || (o.role !== "host" && o.role !== "joined")) continue;
    out.push({
      room_id: id,
      role: o.role,
      name: str(o.name),
      host: str(o.host),
      at,
      handle: str(o.handle),
      ended: num(o.ended),
      seated: num(o.seated) ?? 0,
      first: num(o.first) ?? at,
      created: num(o.created),
      passive: o.passive === true,
    });
  }
  return out;
}

/** MAJLIS-145 (UI-C35): a row's start time - the room's own creation (P-35 `created`), else this phone's first visit. */
export const startOf = (r: Pick<YourRoom, "created" | "first">): number => r.created ?? r.first;

/** Most recent first, the long-ended dropped, at most YOUR_ROOMS_MAX. */
function tidy(list: YourRoom[], now: number): YourRoom[] {
  return list
    .filter((r) => r.ended === null || now - r.ended < ENDED_ROOM_KEEP_MS)
    .sort((a, b) => b.at - a.at)
    .slice(0, YOUR_ROOMS_MAX);
}

export function readYourRooms(storage: KeyValue | null, now: number): YourRoom[] {
  let raw: string | null;
  try {
    raw = storage?.getItem(YOUR_ROOMS_KEY) ?? null;
  } catch {
    return []; // storage refused (private mode, blocked site data): nothing is remembered
  }
  return tidy(parse(raw), now);
}

function write(storage: KeyValue | null, list: YourRoom[]): void {
  try {
    storage?.setItem(YOUR_ROOMS_KEY, JSON.stringify(list));
  } catch {
    // storage refused: the phone simply forgets
  }
}

/** A join landed (joined.room_id): this room is the most recent, with the role the server gave. */
export function rememberVisit(
  storage: KeyValue | null,
  now: number,
  visit: { room_id: string; role: "host" | "joined"; host: string | null },
): YourRoom[] {
  const list = readYourRooms(storage, now);
  const old = list.find((r) => r.room_id === visit.room_id);
  const row: YourRoom = {
    room_id: visit.room_id,
    role: visit.role,
    name: old?.name ?? null,
    host: visit.host ?? old?.host ?? null,
    at: now,
    handle: old?.handle ?? null,
    ended: null,
    seated: old?.seated ?? 0,
    first: old?.first ?? now,
    created: old?.created ?? null,
    passive: old?.passive ?? false,
  };
  const next = tidy([row, ...list.filter((r) => r.room_id !== visit.room_id)], now);
  write(storage, next);
  return next;
}

/** room_state / room_updated inside the room: its name, listing handle and (MAJLIS-145, P-38) whether it is passive, as
 * the server gives them now. */
export function noteRoom(
  storage: KeyValue | null,
  now: number,
  roomId: string,
  fields: { name: string | null; handle: string | null; passive: boolean },
): YourRoom[] {
  const list = readYourRooms(storage, now);
  const next = list.map((r) =>
    r.room_id === roomId ? { ...r, name: fields.name, handle: fields.handle, passive: fields.passive } : r,
  );
  write(storage, next);
  return next;
}

/** The server's answer to POST /api/rooms/mine for every id asked: a room it left out is dropped; an ended one is kept
 * (muted) from the first time the phone learns it ended; names and counts are refreshed. */
export function applyMine(storage: KeyValue | null, now: number, answer: readonly MineRoom[]): YourRoom[] {
  const byId = new Map(answer.map((m) => [m.room_id, m]));
  const next: YourRoom[] = [];
  for (const r of readYourRooms(storage, now)) {
    const m = byId.get(r.room_id);
    if (!m) continue;
    next.push({
      ...r,
      role: m.role,
      name: m.name,
      host: m.host_name || r.host,
      ended: m.active ? null : (r.ended ?? now),
      seated: m.seated,
      handle: m.active ? r.handle : null,
      created: isoMs(m.created) ?? r.created,
    });
  }
  const tidied = tidy(next, now);
  write(storage, tidied);
  return tidied;
}
